import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { fDT } from '../lib/format';
import { CloseX, ErrorBox, useDrawer } from '../ui/kit';

export type Integration = {
  provider: string;
  label: string;
  badge: string | null;
  colour: string | null;
  mode: 'webhook' | 'poll';
  status: 'ok' | 'warn' | 'error';
  last_event_at: string | null;
  last_error: string | null;
  detail: string | null;
};

export function useIntegrations() {
  return useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('integration_health')
        .select('provider, label, badge, colour, mode, status, last_event_at, last_error, detail')
        .order('sort');
      if (error) throw new Error(error.message);
      return data as Integration[];
    },
    refetchInterval: 60_000,
  });
}

function ago(iso: string): string {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}

function headline(x: Integration): string {
  if (x.status === 'error') return x.last_error ?? 'Stopped sending data';
  if (!x.last_event_at) return x.status === 'ok' ? 'Active' : 'Not connected yet';
  return `Last event ${ago(x.last_event_at)}`;
}

const DOT = { ok: 'ok', warn: 'warn', error: 'err' } as const;

export function Integrations() {
  const q = useIntegrations();
  const { open } = useDrawer();
  return (
    <>
      <div className="head">
        <div>
          <h1>
            <span className="grad">Integrations</span>
          </h1>
          <p>If something stops sending data, you see it here first.</p>
        </div>
      </div>
      <ErrorBox error={q.error} />
      {q.isLoading && <div className="card loading">Loading…</div>}
      <div className="integ">
        {(q.data ?? []).map((x) => (
          <section key={x.provider} className="card">
            <div className="top">
              <span className="logo2" style={{ background: x.colour ?? '#697284' }} aria-hidden>
                {x.badge}
              </span>
              <div style={{ flex: 1 }}>
                <h3>{x.label}</h3>
              </div>
              <span
                className={`sdot ${DOT[x.status]}`}
                role="img"
                aria-label={x.status === 'ok' ? 'Working' : x.status === 'warn' ? 'Check' : 'Error'}
              />
            </div>
            <div className="meta">
              <b style={{ color: x.status === 'ok' ? 'var(--ink)' : '#8a5200' }}>{headline(x)}</b>
            </div>
            <div className="meta">
              {x.detail}
              {x.detail ? ' · ' : ''}
              {x.mode === 'webhook' ? 'Pushes events' : 'Checked on a schedule'}
            </div>
            {x.status === 'error' && (
              <div style={{ marginTop: 14 }}>
                <button
                  className="btn sm primary"
                  onClick={() => open({ type: 'fix', provider: x.provider })}
                >
                  Fix
                </button>
              </div>
            )}
          </section>
        ))}
      </div>
    </>
  );
}

// What a person can actually do about each provider, in order.
const FIX: Record<string, { steps: string[]; link?: [string, string] }> = {
  readai: {
    steps: [
      'Open Read.ai, Integrations, Webhooks, and check the webhook status.',
      'If it says Stopped, delete it and create it again with the same URL, then send the new signing key to whoever runs the desk.',
      'Calls recorded meanwhile can be pushed again from each report: Share, Webhooks.',
    ],
    link: [
      'Open Read.ai integrations',
      'https://app.read.ai/analytics/integrations/user/workflow/webhooks',
    ],
  },
  whatsapp: {
    steps: [
      'Rewrite the rejected template without offers or prices.',
      'Submit it again as Utility in WhatsApp Manager.',
      'Approval takes minutes to 24 h. The automation resumes by itself.',
    ],
    link: ['Open WhatsApp Manager', 'https://business.facebook.com/wa/manage/message-templates/'],
  },
};

export function FixDrawer({ provider }: { provider: string }) {
  const q = useIntegrations();
  const x = q.data?.find((i) => i.provider === provider);
  const fix = FIX[provider];
  return (
    <>
      <div className="dhead">
        <div>
          <h2 id="dtitle">{x?.label ?? 'Integration'}</h2>
          {x && <div className="sm">{headline(x)}</div>}
        </div>
        <CloseX />
      </div>
      <div className="dbody">
        <div className="blk">
          <h3>
            <span className="lbl g">Last error</span>
          </h3>
          <div className="panel">
            <p>
              <b>{x?.last_error ?? 'No error recorded.'}</b>
            </p>
            {x?.last_event_at && (
              <p style={{ color: 'var(--muted)', fontSize: 14 }}>
                Last good event: {fDT(x.last_event_at)}
              </p>
            )}
          </div>
        </div>
        <div className="blk">
          <h3>
            <span className="lbl g">How to fix</span>
          </h3>
          <div className="panel">
            {fix ? (
              <ol style={{ margin: 0, paddingLeft: 18 }}>
                {fix.steps.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ol>
            ) : (
              <p>Check the provider's dashboard, then tell Viliam what it says.</p>
            )}
          </div>
        </div>
        {fix?.link && (
          <a className="btn primary" href={fix.link[1]} target="_blank" rel="noreferrer">
            {fix.link[0]}
          </a>
        )}
      </div>
    </>
  );
}
